// wsClient.js
let ws = null;
let listeners = new Set();
let reconnectTimer = null;
let heartbeatInterval = null;
let lastConfig = null;
let tokenExpired = false;
let reconnectAttempts = 0;
let lastPongTime = null;
let isConnecting = false;
const RAPID_RETRY_LIMIT = 5;
const SLOW_RETRY_INTERVAL = 60000; // After rapid retries fail, try every 60s
const HEARTBEAT_INTERVAL = 25000;
const PONG_TIMEOUT = 35000;
const MAX_RECONNECT_DELAY = 30000;

function isTokenExpired(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp && payload.exp * 1000 < Date.now() - 10000) {
      return true;
    }
    return false;
  } catch {
    return true;
  }
}

export function ensureWS(url, token) {
  if (tokenExpired) {
    console.log("[ws] token marked as expired, not reconnecting. Please re-login.");
    return null;
  }

  if (isTokenExpired(token)) {
    console.log("[ws] token is expired (checked locally), not connecting");
    tokenExpired = true;
    return null;
  }

  if (isConnecting) return ws;

  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    window.__physioWs = ws;
    return ws;
  }

  lastConfig = { url, token };
  isConnecting = true;

  try {
    ws = new WebSocket(url, token);
  } catch (e) {
    console.log("[ws] failed to create WebSocket:", e.message);
    isConnecting = false;
    return null;
  }
  
  window.__physioWs = ws;

  ws.onopen = () => {
    console.log("[ws] singleton connected");
    isConnecting = false;
    reconnectAttempts = 0;
    lastPongTime = Date.now();
    startHeartbeat();
  };

  ws.onmessage = (ev) => {
    try {
      const data = JSON.parse(ev.data);
      if (data.event && !data.type?.includes('pong')) {
        console.log("[ws] message received:", data.event);
      }
      
      if (data.type === 'pong') {
        lastPongTime = Date.now();
        return;
      }
      
      if (data.type === 'auth_error') {
        console.log("[ws] auth error:", data.code, data.message);
        tokenExpired = true;
        stopHeartbeat();
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
        isConnecting = false;
        return;
      }
    } catch {}
    listeners.forEach(fn => fn(ev));
  };

  ws.onerror = (e) => {
    console.log("[ws] singleton error");
    isConnecting = false;
  };

  ws.onclose = (event) => {
    isConnecting = false;
    stopHeartbeat();
    
    // Auth failure: stop permanently
    if (event.reason === 'Token expired' || event.reason === 'Invalid token' || event.code === 1008) {
      console.log("[ws] closed due to auth issue (code:", event.code, "reason:", event.reason, "), not retrying");
      tokenExpired = true;
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
      return;
    }

    if (tokenExpired) {
      console.log("[ws] token expired, not retrying");
      return;
    }

    // Re-check token before scheduling reconnect
    if (lastConfig && isTokenExpired(lastConfig.token)) {
      console.log("[ws] token expired before reconnect, stopping");
      tokenExpired = true;
      return;
    }

    // Network failure: keep retrying (fast first, then slow)
    reconnectAttempts++;
    
    let delay;
    if (reconnectAttempts <= RAPID_RETRY_LIMIT) {
      delay = Math.min(2000 * Math.pow(1.5, reconnectAttempts - 1), MAX_RECONNECT_DELAY);
      console.log(`[ws] closed (code: ${event.code}), retrying in ${Math.round(delay/1000)}s (attempt ${reconnectAttempts}/${RAPID_RETRY_LIMIT})`);
    } else {
      delay = SLOW_RETRY_INTERVAL;
      console.log(`[ws] closed (code: ${event.code}), slow retry in ${delay/1000}s (attempt ${reconnectAttempts})`);
    }

    // Reset rapid counter after slow backoff cycle succeeds or every 5 slow retries
    if (reconnectAttempts > RAPID_RETRY_LIMIT + 5) {
      reconnectAttempts = RAPID_RETRY_LIMIT + 1;
    }

    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => {
      ws = null;
      if (lastConfig && !tokenExpired) {
        if (isTokenExpired(lastConfig.token)) {
          console.log("[ws] token expired before reconnect, stopping");
          tokenExpired = true;
          return;
        }
        ensureWS(lastConfig.url, lastConfig.token);
      }
    }, delay);
  };

  return ws;
}

function startHeartbeat() {
  stopHeartbeat();
  
  heartbeatInterval = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        if (lastPongTime && (Date.now() - lastPongTime) > PONG_TIMEOUT) {
          console.log("[ws] no pong received within timeout, reconnecting...");
          ws.close();
          return;
        }
        ws.send(JSON.stringify({ type: 'ping' }));
      } catch (e) {
        console.log("[ws] error sending ping:", e);
        ws.close();
      }
    } else {
      stopHeartbeat();
    }
  }, HEARTBEAT_INTERVAL);
}

function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

export function resetWsAuth(newToken = null) {
  console.log("[ws] resetWsAuth called, clearing expired state");
  tokenExpired = false;
  reconnectAttempts = 0;
  isConnecting = false;
  clearTimeout(reconnectTimer);
  reconnectTimer = null;
  stopHeartbeat();
  
  if (ws) {
    try { ws.close(1000, 'Token refresh'); } catch {}
    ws = null;
  }
  
  if (newToken && lastConfig) {
    lastConfig.token = newToken;
  }
  
  if (lastConfig) {
    ensureWS(lastConfig.url, lastConfig.token);
  }
}

export function updateWsToken(newToken) {
  if (lastConfig) {
    lastConfig.token = newToken;
    if (tokenExpired) {
      resetWsAuth(newToken);
    }
  }
}

export function closeWS() {
  console.log("[ws] closeWS called");
  tokenExpired = true;
  isConnecting = false;
  clearTimeout(reconnectTimer);
  reconnectTimer = null;
  stopHeartbeat();
  if (ws) {
    try { ws.close(1000, 'Client closed'); } catch {}
    ws = null;
  }
  window.__physioWs = null;
}

export function getWSState() {
  if (!ws) return 'CLOSED';
  switch (ws.readyState) {
    case WebSocket.CONNECTING: return 'CONNECTING';
    case WebSocket.OPEN: return 'OPEN';
    case WebSocket.CLOSING: return 'CLOSING';
    case WebSocket.CLOSED: return 'CLOSED';
    default: return 'UNKNOWN';
  }
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
